/**
 * Skeleton Loading Handler
 * Adds 'is-loaded' class to images when they finish loading
 * to trigger the fade-in transition from skeleton to image
 */

(function() {
  'use strict';

  // Selectors for images that should have skeleton loading
  const SKELETON_IMAGE_SELECTORS = [
    '.card-product__media img',
    '.card-collection__media img',
    '.hero__media img',
    '.hero__media picture img',
    '.article-card__image-wrapper img',
    '.article-card__media img',
    '.product-thumbnail__image img',
    '.product__media img',
    '.product-gallery__item img',
    '.product-gallery__slide img',
    '.offer-tile__media img',
    '.banner__media img',
    '.image-block__media img',
    '.slideshow__media img',
    '.product-quick-view-modal__media img',
    '.skeleton-image'
  ].join(', ');

  // Container selectors for fallback (browsers without :has() support)
  const SKELETON_CONTAINER_SELECTORS = [
    '.card-product__media',
    '.card-collection__media',
    '.hero__media',
    '.article-card__image-wrapper',
    '.article-card__media',
    '.product-thumbnail__image',
    '.product__media',
    '.product-gallery__item',
    '.product-gallery__slide',
    '.offer-tile__media',
    '.banner__media',
    '.image-block__media',
    '.slideshow__media',
    '.product-quick-view-modal__media',
    '.skeleton-container'
  ];

  /**
   * Check if browser supports :has() selector
   */
  const supportsHas = CSS.supports('selector(:has(*))');

  /**
   * Mark an image as loaded
   * @param {HTMLImageElement} img
   */
  function markImageLoaded(img) {
    img.classList.add('is-loaded');
    
    // For browsers without :has() support, also mark the container
    if (!supportsHas) {
      const container = img.closest(SKELETON_CONTAINER_SELECTORS.join(', '));
      if (container) {
        container.classList.add('is-loaded');
      }
    }
  }

  /**
   * Handle image load event
   * @param {Event} event
   */
  function handleImageLoad(event) {
    const img = event.target;
    if (img.tagName === 'IMG' && img.matches(SKELETON_IMAGE_SELECTORS)) {
      markImageLoaded(img);
    }
  }

  /**
   * Initialize skeleton loading for existing images
   */
  function initExistingImages() {
    const images = document.querySelectorAll(SKELETON_IMAGE_SELECTORS);
    
    images.forEach(img => {
      // If image is already loaded (cached), mark it immediately
      if (img.complete && img.naturalWidth > 0) {
        markImageLoaded(img);
      } else {
        // Add load listener for images still loading
        img.addEventListener('load', () => markImageLoaded(img), { once: true });
        // Handle error case - still show the skeleton disappear
        img.addEventListener('error', () => markImageLoaded(img), { once: true });
      }
    });
  }

  /**
   * Observe for new images added to the DOM (infinite scroll, dynamic content)
   */
  function observeNewImages() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node is an image
            if (node.tagName === 'IMG' && node.matches(SKELETON_IMAGE_SELECTORS)) {
              if (node.complete && node.naturalWidth > 0) {
                markImageLoaded(node);
              } else {
                node.addEventListener('load', () => markImageLoaded(node), { once: true });
                node.addEventListener('error', () => markImageLoaded(node), { once: true });
              }
            }
            
            // Check for images within the added node
            const images = node.querySelectorAll ? node.querySelectorAll(SKELETON_IMAGE_SELECTORS) : [];
            images.forEach(img => {
              if (img.complete && img.naturalWidth > 0) {
                markImageLoaded(img);
              } else {
                img.addEventListener('load', () => markImageLoaded(img), { once: true });
                img.addEventListener('error', () => markImageLoaded(img), { once: true });
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialize when DOM is ready
   */
  function init() {
    initExistingImages();
    observeNewImages();
    
    // Also use event delegation for dynamically loaded images
    document.addEventListener('load', handleImageLoad, true);
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

